import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Grid,
	Typography,
	TextField
} from '@mui/material';
import './alert-kpi.scss';
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { checkRole } from 'utils/roleUtils';
import { Add, Clear } from '@mui/icons-material';
import { AlertList } from './AlertList';
import { getConfigList, getFilterOptions } from 'service/service';
import Loader from 'components/Loader/loader';
import moment from 'moment';
import PageWrapper from 'components/Common/PageWraper';
import { useTheme } from '@mui/material/styles';
import CustomSelectDropdown from 'components/Dropdown/CustomSelectDropdown';
import OutlinedButton from 'components/Button/OutlinedButton';

const AlertFramework = () => {
	const theme = useTheme();
	const [filterData, setFilterData] = useState<any>({})
	const [loading, setLoading] = useState(false)
	const [alertConfigs, setAlertConfigs] = useState([])
	const [count, setCount] = useState(0)
	const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10)
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [cachedData, setCachedData] = useState<any>(null);
	
	const [controller] = usePepwiseAuthController();
	const { role } = controller;
	const allFilters = ['Program','Project', 'Market', 'Sector','Alert Name','Alert Type','Status', 'Source']
	const filterOptions :any = {
		'Program': { columnName: 'program', data: filterData.programs || []},
		'Project': { columnName: 'project', data: filterData.projects || []},
		'Market': { columnName: 'market', data: filterData.markets || []},
		'Sector': { columnName: 'sector', data: filterData.sectors || []},
		'Alert Name':  { columnName: 'ruleName'},
		'Alert Type':  { columnName: 'alertType', data: filterData.alertTypes || []},
		'Status': { columnName: 'isActive', data: ['Active','Inactive']},
		'Source': { columnName: 'source', data: ['SQL', 'Log Analytics', 'Elastic Search', 'Cosmos']},
	}
	const [filters, setFilters] = useState([
		{ uuid: crypto.randomUUID(), field: 'Program', value: '' }
	]);

	const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);

	const handleAddFilter = () => {
		setFilters([...filters, { uuid: crypto.randomUUID(), field: '', value: '' }]);
	};

	const handleRemoveFilter = (uuid: string) => {
		setFilters(filters.filter((filter: any) => filter.uuid !== uuid));
	};

	const handleFilterChange = (uuid: string, name: string, value: string|null) => {
		setFilters(filters.map((filter:any) => {
			if(filter.uuid === uuid){
				filter[name] = value
				if(name === 'field' && filter.value){
					filter.value = ''
				}
			}
			return filter
		}))
	}

	const onCreateProgramClick = () => {
		window.location.href = '/alertFramework/createalert';
	};
	const handleSorting = (newSortingInfo: any) => {
		setSortingInfo(newSortingInfo);
		fetchAlertConfigs({ ...defaultBody, sortColumn: newSortingInfo.sortBy, sortDir: newSortingInfo.sortDir });
	};
	
	const defaultBody = {page: 1, pageSize: 10, sortColumn: sortingInfo.sortBy , sortDir: sortingInfo.sortDir}

	const fetchAlertConfigs = async ({page, pageSize, sortColumn, sortDir} :any, clearFilters = false) => {
		const savedFilters = localStorage.getItem('alertFilters');
		const filtersFromStorage = savedFilters ? JSON.parse(savedFilters) : [{ uuid: crypto.randomUUID(), field: 'Program', value: '' }];

		// Check if the filter is the default one
  		const isDefaultFilter = filtersFromStorage.length === 1 && filtersFromStorage[0].field === 'Program' && filtersFromStorage[0].value === '';
			
		const filterObj = clearFilters ? {} : filtersFromStorage.filter((fl: any)=> !!fl.value).reduce((filObj:any, fil: any) => {
			const columnName = filterOptions[fil.field].columnName
			if(columnName === 'isActive'){
				filObj[columnName] = fil.value === 'Active' ? true : false
			}else{
				filObj[columnName] = fil.value
			}
			return filObj
		}, {})
		setLoading(true)

		if (isDefaultFilter) {
			if (cachedData) {
			setAlertConfigs(cachedData.data);
			setCount(cachedData.totalCount);
			setLoading(false);
			return; // Skip API call if data is cached
			}
		}

		if(sortColumn === 'updatedDt'){
			sortColumn = 'updatedAt'
		}else if(sortColumn === 'createdDt'){
			sortColumn = 'createdAt'
		}
		const response = await getConfigList({filter: filterObj, page, pageSize, sortColumn, sortDir})
		setLoading(false)
		if(response?.data){

			// Cache data in state if it's the default filter
			if (isDefaultFilter) {
				setCachedData({
					data: response.data.data,
					totalCount: response.data.totalCount,
				});
			}

			response.data.data = response.data.data.map((item: any) => {
				item.updatedDt = moment(item.updatedAt).format('yyyy-MM-DD HH:mm')
				item.createdDt = moment(item.createdAt).format('yyyy-MM-DD HH:mm')
				item.createdByUser = item.createdByUser?.[0] || ''
				item.updatedByUser = item.updatedByUser?.[0] || ''
				return item
			})
			setAlertConfigs(response.data.data)
			setCount(response.data.totalCount)
		}
	}

	const fetchOptions = async () => {
		const response = await getFilterOptions()
		if(response.data){
			setFilterData(response.data)
		}
	}

	const clearFilters = () => {
		const defaultFilter = [{ uuid: crypto.randomUUID(), field: 'Program', value: '' }];
		setFilters(defaultFilter);
		localStorage.setItem('alertFilters', JSON.stringify(defaultFilter));
		fetchAlertConfigs(defaultBody, true)
	}

	const handleRunQuery = () => {
		localStorage.setItem('alertFilters', JSON.stringify(filters));
		fetchAlertConfigs(defaultBody);
	};

	useEffect(() => {
		const savedFilters = localStorage.getItem('alertFilters');
		if (savedFilters) {
			setFilters(JSON.parse(savedFilters));
		}else {
			setFilters([{ uuid: crypto.randomUUID(), field: 'Program', value: '' }]);
		}
		fetchAlertConfigs(defaultBody)
		fetchOptions()
	},[])

	return (
		<PageWrapper>
			<PageHeader title="Alerts" icon={DashboardIcon}>
				{isAllowed && (
					<Button variant="pepwiseSecondary" onClick={onCreateProgramClick} sx={{ height: '40px' }}>
						CREATE ALERT
					</Button>
				)}
			</PageHeader>

			<Box
				sx={{
					background: theme.palette.queryBuilder.tableCellBackground,
					margin: 2,
					padding: 2,
					borderRadius: 2
				}}
			>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography
						variant="subHeader"
						gutterBottom
						sx={{
							color: theme.palette.inputNew.labelText
						}}
					>
						Filters
					</Typography>
				</Box>

				<Box
					sx={{
						width: '100%',
						height: '1px',
						backgroundColor: theme.palette.inputNew.inputBorder,
						opacity: 0.3,
						mb: 1
					}}
				/>

				<Box
					display="flex"
					alignItems="center"
					sx={{
						width: 'fit-content',
						cursor: 'pointer',
						mb: 1
					}}
					onClick={clearFilters}
				>
					<Clear
						sx={{
							fontSize: '18px',
							color: theme.palette.inputNew.inputPlaceholder,
							mr: 0.5
						}}
					/>

					<Typography
						variant="bodyText"
						sx={{
							color: theme.palette.inputNew.inputPlaceholder,
							fontSize: '14px',
							fontWeight: 500
						}}
					>
						CLEAR ALL
					</Typography>
				</Box>

				<Grid container spacing={1} alignItems="center">
					<Grid container item spacing={1} alignItems="center">
						<Grid item xs={1.2}></Grid>

						<Grid item xs={5.2}>
							<Typography
								variant='bodyText'
								sx={{
									color: theme.palette.inputNew.labelText
								}}
							>
								Field
							</Typography>
						</Grid>

						<Grid item xs={5.6}>
							<Typography
								variant='bodyText'
								sx={{
									color: theme.palette.inputNew.labelText
								}}
							>
								Values
							</Typography>
						</Grid>
					</Grid>

					{filters.map((filter, index) => (
						<Grid container item spacing={1} alignItems="center" key={filter.uuid}>
							<Grid item xs={1.2}>
								<Box display="flex" justifyContent="left" gap={1}>
									<OutlinedButton
										disabled={filters.length === 1}
										sx={{
											color: filters.length === 1
												? theme.palette.inputNew.inputPlaceholder
												: theme.palette.inputNew.removeIcon,
											borderColor: filters.length === 1
												? theme.palette.inputNew.inputPlaceholder
												: theme.palette.inputNew.removeBorder,
											width: 30,
											height: 30,
											'& .MuiSvgIcon-root': {
												color: filters.length === 1
													? theme.palette.inputNew.inputPlaceholder
													: theme.palette.inputNew.removeIcon
											},
											'&:hover': {
												backgroundColor: filters.length === 1
													? 'transparent'
													: theme.palette.inputNew.removeHoverBackground,
												borderColor: filters.length === 1
													? theme.palette.inputNew.inputPlaceholder
													: theme.palette.inputNew.removeBorder
											}
										}}
										onClick={() => handleRemoveFilter(filter.uuid)}
									>
										<Clear />
									</OutlinedButton>

									<OutlinedButton
										sx={{
											color: theme.palette.inputNew.addText,
											borderColor: theme.palette.inputNew.addText,
											width: 30,
											height: 30,
											'& .MuiSvgIcon-root': {
												color: theme.palette.inputNew.addText
											}
										}}
										onClick={handleAddFilter}
									>
										<Add />
									</OutlinedButton>
								</Box>
							</Grid>

							<Grid item xs={5.2}>
								<CustomSelectDropdown
									options={allFilters
										.filter((option) =>
											option === filter.field ||
											!filters.map(f => f.field).includes(option)
										)
										.map((item) => ({
											value: item,
											label: item
										}))
									}
									handleChange={(e) =>
										handleFilterChange(
											filter.uuid,
											'field',
											e.target.value
										)
									}
									value={filter.field || ''}
									selectComponentProps={{
										displayEmpty: true,
										sx: {
											height: '32px !important',

											'& .MuiSelect-select': {
												color: `${theme.palette.DropdownV2.menuItemText} !important`,
												fontWeight: '500 !important'
											},

											'& .MuiBox-root': {
												fontWeight: '500 !important'
											}
										}
									}}
								/>
							</Grid>

							{!!filter.field && 
							<Grid item xs={5.6}>
								{filterOptions[filter.field]?.data ? 
								<CustomSelectDropdown
									options={(filterOptions[filter.field]?.data || []).map((item: any) => ({
										value: item,
										label: item
									}))}
									handleChange={(e) =>
										handleFilterChange(
											filter.uuid,
											'value',
											e.target.value
										)
									}
									value={filter.value || ''}
									selectComponentProps={{
										displayEmpty: true,
										sx: {
											height: '32px !important',

											'& .MuiSelect-select': {
												color: `${theme.palette.DropdownV2.menuItemText} !important`,
												fontWeight: '500 !important'
											},

											'& .MuiBox-root': {
												fontWeight: '500 !important'
											}
										}
									}}
								/> :
								<TextField
									placeholder="Enter value"
									variant="outlined"
									value={filter.value || ''}
									onChange={(e) => handleFilterChange(filter.uuid, 'value', e.target.value)}
									sx={{
										width: '100%',

										'& .MuiOutlinedInput-root': {
											height: '32px',
											backgroundColor: theme.palette.inputNew.inputBackground,
											borderRadius: '4px',

											'& fieldset': {
												borderColor: theme.palette.inputNew.inputBorder
											},

											'&:hover fieldset': {
												borderColor: theme.palette.inputNew.inputBorderHover
											},

											'&.Mui-focused fieldset': {
												borderColor: theme.palette.inputNew.inputBorderHover
											}
										},

										'& .MuiOutlinedInput-input': {
											color: theme.palette.inputNew.inputText,
											fontSize: '14px',
											fontWeight: 500,
											fontFamily: 'Montserrat',
											padding: '6px 12px',

											'&::placeholder': {
												color: theme.palette.inputNew.inputPlaceholder,
												opacity: 1
											}
										}
									}}
								/>}
							</Grid>}
						</Grid>
					))}
				</Grid>

				<Box
					sx={{
						width: '100%',
						height: '1px',
						backgroundColor: theme.palette.inputNew.inputBorder,
						opacity: 0.5,
						mt: 2
					}}
				/>

				<Box
					display="flex"
					justifyContent="flex-end"
					mt={2}
				>
					<Button
						variant="outlined"
						color='secondary'
						onClick={handleRunQuery}
						sx={{
							color: theme.palette.inputNew.addText,
							borderColor: theme.palette.inputNew.addText,
							fontWeight: 500,

							'&:hover': {
								borderColor: theme.palette.inputNew.inputBorderHover,
								backgroundColor: theme.palette.inputNew.inputBackground
							}
						}}
					>
						RUN QUERY
					</Button>
				</Box>
			</Box>
			{loading ? <Loader/> : <AlertList isAllowed={isAllowed} alertConfigs={alertConfigs} totalCount={count} updateAlerts={fetchAlertConfigs} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} sortingInfo = {sortingInfo} setSortingInfo= {setSortingInfo} handleSorting = {handleSorting}/>}
		</PageWrapper>
	);
};

export default AlertFramework;
